import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Thermometer, MapPin, Award, Boxes, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';

const Distribution = () => {
  const { t } = useLanguage();

  const capabilities = [
    { icon: Thermometer, ...t.distribution.capabilities.items[0] },
    { icon: MapPin, ...t.distribution.capabilities.items[1] },
    { icon: Award, ...t.distribution.capabilities.items[2] },
    { icon: Boxes, ...t.distribution.capabilities.items[3] },
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative py-20 min-h-[60vh] flex items-center">
        <div className="absolute inset-0 z-0">
          <img
            src="https://customer-assets.emergentagent.com/job_mea-connect/artifacts/vzqp632q_image00001%20%281%29.jpg"
            alt="Distribution Network"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#003840]/65 via-[#003840]/55 to-[#003840]/65"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 animate-float">
              {t.distribution.title}
            </h1>
            <p className="text-xl text-gray-100 max-w-3xl mx-auto leading-relaxed animate-fade-in">
              {t.distribution.subtitle}
            </p>
          </div>
        </div>
      </section>

      {/* Intro Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-lg text-gray-600 leading-relaxed">
              {t.distribution.intro}
            </p>
          </div>
        </div>
      </section>

      {/* Distribution Categories */}
      <section className="py-20 bg-[#F4F7F6]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#003840] mb-4">
              {t.distribution.categoriesTitle || 'Our Distribution Categories'}
            </h2>
            <p className="text-xl text-gray-600">
              {t.distribution.categoriesSubtitle || 'Specialized distribution solutions for different product segments'}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* VIYA CRAB Card */}
            <Link to="/distribution/viya-crab" onClick={() => window.scrollTo(0, 0)} className="group">
              <div className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300">
                <div className="h-64 overflow-hidden">
                  <img
                    src="https://customer-assets.emergentagent.com/job_aria-trading/artifacts/srqwgwx5_Colossal%20lump%2002.JPEG"
                    alt="VIYA CRAB"
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                  />
                </div>
                <div className="p-8">
                  <h3 className="text-3xl font-bold text-[#003840] mb-4">VIYA CRAB</h3>
                  <p className="text-gray-600 leading-relaxed mb-6">
                    {t.viyaCrab?.subtitle || 'Premium Pasteurized Crab Meat from Thailand'}
                  </p>
                  <div className="flex items-center text-[#007369] font-semibold group-hover:translate-x-2 transition-transform">
                    <span>{t.distribution.learnMore || 'Learn More'}</span>
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </div>
                </div>
              </div>
            </Link>

            {/* GOLDEN SHEAF Card */}
            <Link to="/distribution/golden-sheaf" onClick={() => window.scrollTo(0, 0)} className="group">
              <div className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300">
                <div className="h-64 overflow-hidden">
                  <img
                    src="https://customer-assets.emergentagent.com/job_aria-trading/artifacts/ov3ey2bv_303005c0-a22e-4016-9d5a-d40105baa892.png"
                    alt="GOLDEN SHEAF"
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                  />
                </div>
                <div className="p-8">
                  <h3 className="text-3xl font-bold text-[#003840] mb-4">{t.agricola?.title || 'GOLDEN SHEAF'}</h3>
                  <p className="text-gray-600 leading-relaxed mb-6">
                    {t.agricola?.subtitle || 'Premium Wheat, Expertly Milled for Reliable Results.'}
                  </p>
                  <div className="flex items-center text-[#007369] font-semibold group-hover:translate-x-2 transition-transform">
                    <span>{t.distribution.learnMore || 'Learn More'}</span>
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </div>
                </div>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Distribution Capabilities */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#003840] mb-4">
              {t.distribution.capabilities.title}
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {capabilities.map((capability, index) => {
              const Icon = capability.icon;
              return (
                <div
                  key={index}
                  className="bg-[#F4F7F6] p-8 rounded-2xl hover:shadow-2xl transition-all duration-300"
                >
                  <div className="p-3 bg-white rounded-lg inline-block mb-4">
                    <Icon className="h-8 w-8 text-[#007369]" />
                  </div>
                  <h3 className="text-2xl font-bold text-[#003840] mb-3">{capability.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{capability.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#003840] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            {t.cta.distribution.title}
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            {t.cta.distribution.subtitle}
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-[#007369] hover:bg-[#005A5B] text-white px-10 py-7 text-lg shadow-2xl">
              {t.cta.distribution.button}
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Distribution;
